import { inject, Injectable } from '@angular/core';
import { NetworkHelper } from '../helpers';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { StorageService } from './storage.service';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Utl } from '../helpers/utl';
import { SHARED_ENV } from '../tokens';

dayjs.extend(utc);

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly env = inject(SHARED_ENV);

  static emptyUser: any = { apiKeys: [''] };
  private readonly hostUrl: string;
  private currentUserChanged: boolean = true;
  private firstLoad: boolean = true;
  currentUser: BehaviorSubject<any> | null = null;

  /** Reactive KYC status shared across all components */
  private readonly _kycStatus$ = new BehaviorSubject<string | null>(null);
  readonly kycStatus$ = this._kycStatus$.asObservable();

  /** Emits fresh user data after a forced refresh (matching mobile UpdateUserEvent) */
  private readonly _userRefresh$ = new Subject<any>();
  readonly userRefresh$ = this._userRefresh$.asObservable();

  constructor(
    private networkHelper: NetworkHelper,
    private storage: StorageService
  ) {
    this.hostUrl = this.env.host;
  }

  resetForLogout(): void {
    this.currentUserChanged = true;
    this.firstLoad = true;
    if (this.currentUser) {
      this.currentUser.next(UserService.emptyUser);
    }
    this._kycStatus$.next(null);
    this.storage.removeItem('isFxClient');
  }

  /**
   * Remembers the account flags the money pages check before acting, from a user the API or the
   * socket returned: `isFxClient` is the user of an FX-client company (no orders, exchange,
   * deposits or withdrawals for now).
   */
  rememberAccountFlags(user: any): void {
    if (user && typeof user.isFxClient === 'boolean') {
      this.storage.setItem('isFxClient', String(user.isFxClient));
    }
  }

  isFxClient(): boolean {
    return this.storage.getItem('isFxClient') === 'true';
  }

  /** Update KYC status reactively — notifies all subscribers and persists to storage */
  setKycStatus(status: string): void {
    // Don't let stale data revert an optimistic 'pending' back to an unverified state
    const current = this._kycStatus$.value;
    if (current === 'pending' && (status === 'new' || status === 'unauthorized')) {
      return;
    }
    this._kycStatus$.next(status);
    this.storage.setItem('kycStatus', status);
  }

  /** Re-fetch user from API and broadcast to all subscribers (matching mobile UpdateUserEvent) */
  refreshUser(userId: string): void {
    this.get(userId).subscribe({
      next: (user: any) => {
        const status = user?.kycStatus || 'unauthorized';
        this.setKycStatus(status);
        this.rememberAccountFlags(user);
        this._userRefresh$.next(user);
      },
    });
  }

  /** Broadcast user data update to all subscribers (e.g. from socket) */
  notifyUserUpdate(user: any): void {
    this._userRefresh$.next(user);
  }

  getCurrentUser(): BehaviorSubject<any> | null {
    if (!this.currentUserChanged) {
      return this.currentUser;
    }

    if (this.firstLoad) {
      this.firstLoad = false;
      this.currentUser = new BehaviorSubject<any>(UserService.emptyUser);
    }

    if (this.currentUserChanged) {
      this.currentUserChanged = false;

      const userId = this.storage.getItem('userId');
      this.get(userId).subscribe(
        (user) => {
          this.currentUser!.next(user.result);
        },
        (error) => {
          this.currentUser!.error(error); // propagate up
        }
      );
    }

    return this.currentUser;
  }

  userInfo(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/user/info`, data);
  }

  getBalance(userId: string, currency: string): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/user/${userId}/balances`, {
      currency: currency,
    });
  }

  /** Daily portfolio valuation snapshots; `startDate` / `endDate` in ms, at most 366 days apart. */
  getPortfolioSnapshots(
    userId: string,
    startDate: number,
    endDate: number
  ): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/user/${userId}/portfolio/snapshots`,
      {
        startDate: String(startDate),
        endDate: String(endDate),
      }
    );
  }

  getBalances(
    userId: string,
    includeSummary: boolean = true,
    includePending: boolean = true,
    includePnl: boolean = true
  ): Observable<any> {
    return this.networkHelper
      .get(`${this.hostUrl}/user/${userId}/balances`, {
        includeSummary: includeSummary,
        includePending: includePending,
        includePnl: includePnl,
      })
      .pipe(
        map((result: any) => {
          const balances = result;

          const userBalances: any[] = [];
          let baseFiatCurrency = '';
          for (const currency in balances.total) {
            if (!balances.total.hasOwnProperty(currency)) {
              continue;
            }
            const balance: any = {};
            balance.total = balances.total[currency];
            if (includePending) {
              balance.pending = balances.pending[currency];
            }
            balance.available = balances.available[currency];
            balance.currency = currency;
            balance.name = balances.currencyDetails[currency].name;
            balance.iconUrl = balances.currencyDetails[currency].iconUrl;
            balance.depositActive =
              balances.currencyDetails[currency].depositActive;
            balance.withdrawActive =
              balances.currencyDetails[currency].withdrawActive;
            balance.isCrypto =
              balances.currencyDetails[currency].type === 'crypto';
            balance.type = balances.currencyDetails[currency].type;
            balance.isin = balances.currencyDetails[currency].isin ?? null;
            balance.assetClassId =
              balances.currencyDetails[currency].assetClassId ?? null;
            balance.descriptionAssetClass =
              balances.currencyDetails[currency].descriptionAssetClass ?? null;
            // OTC holdings are not held at a custodian.
            balance.isOtc = balances.currencyDetails[currency].isOtc === true;
            // { GTN: '6', STONEX: '4' } when the holding is split across
            // custodians, otherwise null (everything at GTN).
            balance.custody = balances.custody?.[currency] ?? null;

            if (includeSummary && balances.summaries?.[currency]) {
              const baseCurrencies = Object.keys(balances.summaries[currency]);
              const btcIndex = baseCurrencies.indexOf('BTC');
              if (btcIndex !== -1) {
                baseCurrencies.splice(btcIndex, 1);
              }
              if (baseCurrencies.length) {
                baseFiatCurrency = baseCurrencies[0];
              }

              balance.summary = {};
              if (balances.summaries[currency].BTC != null) {
                balance.summary.BTC = balances.summaries[currency].BTC;
              }
              if (baseFiatCurrency && balances.summaries[currency][baseFiatCurrency] != null) {
                balance.summary[baseFiatCurrency] =
                  balances.summaries[currency][baseFiatCurrency];
              }
            }
            userBalances.push(balance);
          }

          if (includeSummary) {
            return {
              wallets: userBalances,
              summary: balances.summary,
              baseFiatCurrency: baseFiatCurrency,
              pnl: includePnl ? balances.pnl : undefined,
            };
          } else {
            return { wallets: userBalances };
          }
        })
      );
  }

  getReferralFees(userId: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/user/${userId}/referral-fees`
    );
  }

  getReferralCount(userId: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/user/${userId}/referral-count`
    );
  }

  getReferenceToken(userId: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/user/${userId}/reference-token`
    );
  }

  /**
   * Render a contract in the given language. Resolves to
   * `{ fileName, contentType, content (base64), contractDate }`. `contractDate`
   * is only read by the server when the client has none stored, and is then
   * saved for good unless `saveContractDate` is false.
   */
  downloadContract(
    userId: string,
    payload: {
      type: 'main' | 'custody';
      language: string;
      contractDate?: string;
      /** false prints `contractDate` on this download only, without storing it. */
      saveContractDate?: boolean;
    }
  ): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/user/${userId}/contract`,
      payload
    );
  }

  getPresignedUrlForImageDownload(url: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/user/s3/presign-download`, {
      url: url,
    });
  }

  getReferralsCountByDate(
    startDate: number,
    endDate: number,
    userOrEmail?: string
  ): Observable<any> {
    const data: any = {
      startDate: startDate,
      endDate: endDate,
    };

    if (userOrEmail) {
      if (Utl.isValidEmail(userOrEmail)) {
        data.email = userOrEmail;
      } else {
        data.userId = userOrEmail;
      }
    }

    return this.networkHelper.post(
      `${this.hostUrl}/user/referral-count-by-date`,
      data
    );
  }

  getReferralsTradesByLevel(
    startDate: number,
    endDate: number,
    userOrEmail?: string
  ): Observable<any> {
    const data: any = {
      startDate: startDate,
      endDate: endDate,
    };

    if (userOrEmail) {
      if (Utl.isValidEmail(userOrEmail)) {
        data.email = userOrEmail;
      } else {
        data.userId = userOrEmail;
      }
    }

    return this.networkHelper.post(
      `${this.hostUrl}/user/referral-trades-by-level`,
      data
    );
  }

  getReferralsTradesPerLevelByDate(
    startDate: number,
    endDate: number,
    userOrEmail?: string
  ): Observable<any> {
    const data: any = {
      startDate: startDate,
      endDate: endDate,
    };

    if (userOrEmail) {
      if (Utl.isValidEmail(userOrEmail)) {
        data.email = userOrEmail;
      } else {
        data.userId = userOrEmail;
      }
    }

    return this.networkHelper.post(
      `${this.hostUrl}/user/referral-trades-by-date`,
      data
    );
  }

  getFee(
    feeType: string,
    currencyOrPair: string,
    amount: number,
    isInclusive: boolean,
    includeParentFees: boolean,
    paymentProvider?: string
  ): Observable<any> {
    const tmpCurrencyOrPair = currencyOrPair.replace(/\//, '-');
    const query: any = {};
    if (paymentProvider) {
      query.paymentProvider = paymentProvider;
    }

    return this.networkHelper.get(
      `${this.hostUrl}/user/fee/${feeType}/${tmpCurrencyOrPair}/${amount}/${isInclusive}/${includeParentFees}`,
      query
    );
  }

  firstLogin(): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/user/first-login`, {});
  }

  confirmData(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/user/kyc`, data);
  }

  get(userId: string): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/user/${userId}`, {});
  }

  setUserLanguage(lang: string): Observable<any> {
    return this.networkHelper.put(`${this.hostUrl}/user/language`, {
      lang: lang,
    });
  }

  updateUser(user: any): Observable<any> {
    this.currentUserChanged = true;

    return this.networkHelper.put(`${this.hostUrl}/user/${user.id}`, user);
  }

  setPromo(user: any, promoCode: string): Observable<any> {
    return this.networkHelper.put(`${this.hostUrl}/user/promo/${user.id}`, {
      promoCode: promoCode,
    });
  }

  getEstimated(base: string, quote: string): Observable<any> {
    return this.networkHelper.getOut(
      `https://min-api.cryptocompare.com/data/price?fsym=${base}&tsyms=${quote}`
    );
  }

  getAddress(currency: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/user/deposit/${currency}`,
      {}
    );
  }

  getNewAddress(currency: string): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/user/deposit/${currency}`,
      {}
    );
  }

  withdraw(currency: string, amount: number, address: string): Observable<any> {
    const data = {
      currency: currency,
      amount: amount,
      address: address,
    };
    return this.networkHelper.post(`${this.hostUrl}/user/withdraw`, data);
  }

  getUserDocument(
    userId: string,
    dim: string,
    dir: string,
    name: string
  ): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/user/documents/${userId}?dim=${dim}&dir=${dir}&name=${name}`
    );
  }

  confirmWithdraw(token: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/user/approve-withdraw/${token}`
    );
  }

  emailApiKey(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/user/email-api-key`);
  }

  revokeApiKey(index: number): Observable<any> {
    const data = {
      apiKeyIndex: index,
    };
    return this.networkHelper.post(`${this.hostUrl}/user/revoke-api-key`, data);
  }

  prepareDepositFiat(
    provider: string,
    currency: string,
    amount: number
  ): Observable<any> {
    const data = {
      currency: currency,
      amount: amount,
    };
    return this.networkHelper.post(
      `${this.hostUrl}/fiat-payment-system/generate-deposit-data/${provider}`,
      data
    );
  }

  prepareWithdrawFiat(
    provider: string,
    currency: string,
    amount: number,
    address = null
  ): Observable<any> {
    const data: any = {
      currency: currency,
      amount: amount,
    };

    if (address) {
      data['address'] = address;
    }

    return this.networkHelper.post(
      `${this.hostUrl}/fiat-payment-system/withdraw-fiat/${provider}`,
      data
    );
  }

  updateVisits(token: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/user/increment-visits`, {
      referrerToken: token,
    });
  }

  getAllUserTrades(
    symbol: string,
    side: string,
    page: number,
    limit: number,
    startDate: number,
    endDate: number,
    userId: string,
    containTotals: boolean
  ): Observable<any> {
    const data: any = {
      symbol: symbol,
      side: side,
      page: page,
      itemsPerPage: limit,
      startDate: startDate,
      endDate: endDate,
      containTotals: containTotals,
    };

    if (userId) {
      data['userId'] = userId;
    }

    return this.networkHelper.get(`${this.hostUrl}/user/trades`, data);
  }

  getAllUserTradesCsv(
    symbol: string,
    side: string,
    startDate: number,
    endDate: number,
    userId?: string
  ): Observable<any> {
    const tzShift = dayjs().utcOffset();
    const query: any = {
      symbol: symbol,
      side: side,
      startDate: startDate,
      endDate: endDate,
      timeZoneShift: tzShift,
    };
    if (userId) {
      query.userId = userId;
    }

    return this.networkHelper.get(`${this.hostUrl}/user/csv/trades`, query);
  }

  updateUserFavorites(pairs: string[]): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/user/favorite-pairs`, {
      pairs: pairs,
    });
  }

  cancelWithdrawal(withdrawalId: string): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/cancel-withdrawal/${withdrawalId}`,
      {}
    );
  }

  removeReferral(userId: string, referrerUserId: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/user/remove-referral`, {
      userId: userId,
      referrerUserId: referrerUserId,
    });
  }

  getAvailableAirdrops(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/airdrops`);
  }

  receiveAirdrop(currency: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/airdrops/${currency}`, {});
  }

  markForKYCCheck(userId: string, tiersToCheck: number[]) {
    return this.networkHelper.post(`${this.hostUrl}/mark-for-kyc-check`, {
      userId: userId,
      tiersToCheck: tiersToCheck,
    });
  }

  setUsername(userId: string, username: string): Observable<any> {
    return this.networkHelper.patch(`${this.hostUrl}/user/${userId}/username`, {
      username: username,
    });
  }

  setReferralFees(
    userId: string,
    referralFees: Array<number>
  ): Observable<any> {
    return this.networkHelper.patch(
      `${this.hostUrl}/user/${userId}/referral-fees`,
      { referralFees: referralFees }
    );
  }

  blockUnblockUser(
    userId: string,
    type: 'block' | 'unblock',
    reason: string
  ): Observable<any> {
    return this.networkHelper.put(`${this.hostUrl}/user/${userId}/${type}`, {
      reason,
    });
  }

  getIpAddresses(userId: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/auth/ip-addresses`, {
      id: userId,
    });
  }

  updateUserGroupId(userId: string, data: any): Observable<any> {
    return this.networkHelper.patch(
      `${this.hostUrl}/user/group/${userId}`,
      data
    );
  }

  activateUser(userId: string): Observable<any> {
    return this.networkHelper.patch(
      `${this.hostUrl}/user/activate/${userId}`,
      {}
    );
  }

  editProfile(userId: string, payload: any) {
    return this.networkHelper.put(
      `${this.hostUrl}/user/profile/${userId}`,
      payload
    );
  }

  /**
   * Hand-correct name/address translations. Partial by design — send only the
   * leaves being changed (`{ surname: { ru: 'Оганесян' } }`). An empty string
   * resets a leaf and drops its admin override.
   */
  editUserTranslations(userId: string, translations: any) {
    return this.networkHelper.put(`${this.hostUrl}/user/translations/${userId}`, {
      translations,
    });
  }

  /**
   * Re-run the machine translation. `force: true` discards admin corrections
   * for the fields in scope; `fields` defaults to all five on the server.
   */
  refreshUserTranslations(
    userId: string,
    payload: { force?: boolean; fields?: string[] } = {}
  ) {
    return this.networkHelper.post(
      `${this.hostUrl}/user/translations/${userId}`,
      payload
    );
  }

  editQuestionary(userId: string, payload: any) {
    return this.networkHelper.put(
      `${this.hostUrl}/user/questionary/${userId}`,
      payload
    );
  }

  submitQuestionary(payload: any) {
    return this.networkHelper.put(
      `${this.hostUrl}/user/questionary`,
      payload
    );
  }

  deactivateUser(userId: string): Observable<any> {
    return this.networkHelper.patch(
      `${this.hostUrl}/user/deactivate/${userId}`,
      {}
    );
  }

  getTradingSummaryPerSymbol(
    symbol: string,
    startDate: number,
    endDate: number,
    userId: string
  ): Observable<any> {
    const data = {
      symbol: symbol,
      startDate: startDate,
      endDate: endDate,
      userId: userId,
    };

    return this.networkHelper.get(`${this.hostUrl}/user/trades-summary`, data);
  }

  setWithdrawalLimit(userId: string, limit: number): Observable<any> {
    const data = {
      dailyWithdrawal: limit,
    };

    return this.networkHelper.put(
      `${this.hostUrl}/user/${userId}/daily-withdrawal`,
      data
    );
  }

  deleteUser(userId: string): Observable<any> {
    return this.networkHelper.delete(`${this.hostUrl}/user/${userId}`, {});
  }

  getQuestionary(userId: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/user/questionary/${userId}`,
      {}
    );
  }

  updateKycStatus(
    userId: string,
    status: 'approved' | 'rejected' | 'pending' | 'new',
    reason?: string
  ): Observable<any> {
    const payload: any = { status };
    if (reason) {
      payload.reason = reason;
    }
    return this.networkHelper.put(
      `${this.hostUrl}/user/${userId}/kyc`,
      payload
    );
  }

  updateKycDocument(userId: string, payload: any): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/user/${userId}/kyc-documents`,
      payload
    );
  }

  getByBrokerageAccount(brokerageAccount: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/user/brokerage-account/${brokerageAccount}`,
      {}
    );
  }

  editAccountType(userId: string, accountType: 'individual' | 'company') {
    return this.networkHelper.patch(
      `${this.hostUrl}/user/account-type/${userId}`,
      { accountType }
    );
  }

  editCompany(userId: string, payload: any) {
    return this.networkHelper.put(
      `${this.hostUrl}/user/company/${userId}`,
      payload
    );
  }

  initiateAccountDeletion(reason: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/request`, {
      type: 'delete',
      data: { reason },
    });
  }

  confirmAccountDeletion(id: string, code: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/request/${id}/verify`, {
      code,
    });
  }

  resendDeletionCode(id: string): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/request/${id}/resend-verify`,
      {}
    );
  }

  getContactInfo(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/contact`, {});
  }

  estimateBankDeposit(
    userId: string,
    currency: string,
    amount: number
  ): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/user/deposit/bank/estimate`,
      { userId, currency, amount }
    );
  }
}
